import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, ComboboxGroup, Input, Radio, Tooltip } from '@node-space/ui-components'
import { FormFieldsRow } from 'components/Form/FormFieldsRow'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { CountryCodes, type ErrorDetails } from 'types/types'
import { BOOLEAN_RADIO_OPTIONS } from '../helpers/constants'
import { BUSINESS_CUSTOMER_DEFAULT_FORM_DATA } from '../helpers/form'
import { BusinessCustomerFormData, ConfirmBusinessCustomerMethod } from '../helpers/types'
import { scrollToTopError } from '../helpers/utils'
import { useBusinessCustomerForm } from '../hooks/useBusinessCustomerForm'
import { AddCustomerActions } from './AddCustomerActions'

export interface AddBusinessCustomerProps {
  defaultValues: BusinessCustomerFormData
  onConfirm: ConfirmBusinessCustomerMethod
  onCancel: () => void
  fieldErrors?: ErrorDetails
  isReliance: boolean
}

export const AddBusinessCustomer = ({
  defaultValues,
  onConfirm,
  onCancel,
  fieldErrors,
  isReliance,
}: AddBusinessCustomerProps) => {
  const { t } = useTranslation()
  const { accountCapabilities } = useAccountsContext()
  const paxosUsStates = accountCapabilities?.paxosUsStatesCapability || []

  const {
    formFields,
    displayData,
    hasErrors,
    handleSubmit,
    form,
    formValues,
    setValue,
    showPaxosUsStatesFields,
  } = useBusinessCustomerForm(
    defaultValues ?? BUSINESS_CUSTOMER_DEFAULT_FORM_DATA,
    fieldErrors,
    isReliance,
    paxosUsStates
  )
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const onInvalid = () => {
    scrollToTopError(form, fieldRefs)
  }

  const showUsTaxNumberField = isReliance && formValues?.countryOfIncorporation === CountryCodes.US

  const relianceFields = () => {
    if (!isReliance) return null
    if (showUsTaxNumberField) {
      return (
        <Box ref={el => (fieldRefs.current.taxNumber = el)}>
          <Input {...formFields?.taxNumber} />
        </Box>
      )
    }
    return (
      <Box ref={el => (fieldRefs.current.registrationNumber = el)}>
        <Input {...formFields?.registrationNumber} />
      </Box>
    )
  }

  // Adding Reliance address fields
  const relianceAddressFields = () => {
    if (!isReliance) return null
    return (
      <>
        <h3 className="text-h3 text-foreground-prominent pt-2">
          {t('customers.registeredAddress')}
        </h3>
        <Box ref={el => (fieldRefs.current.addressLine1 = el)}>
          <Input {...formFields?.addressLine1} />
        </Box>
        <Box ref={el => (fieldRefs.current.addressLine2 = el)}>
          <Input {...formFields?.addressLine2} optionalText={t('optional')} />
        </Box>
        <FormFieldsRow
          leftField={
            <Box ref={el => (fieldRefs.current.city = el)}>
              <Input {...formFields?.city} />
            </Box>
          }
          rightField={
            <Box ref={el => (fieldRefs.current.postalCode = el)}>
              <Input {...formFields?.postalCode} />
            </Box>
          }
        />
      </>
    )
  }

  const handleRadioChange = (
    field: 'isRegulated' | 'isPubliclyListed',
    value: string,
    dependentFields: (keyof BusinessCustomerFormData)[]
  ) => {
    setValue(field, value as never, { shouldValidate: true })
    if (value) {
      dependentFields.forEach(dep => setValue(dep, '' as never, { shouldValidate: true }))
    }
  }

  useEffect(() => {
    if (!fieldErrors || !hasErrors) return
    scrollToTopError(form, fieldRefs)
  }, [fieldErrors, hasErrors])

  const onSubmit = (data: BusinessCustomerFormData) => {
    const confirmData = { ...data }
    const confirmDisplayData = { ...displayData }
    if (showPaxosUsStatesFields) {
      if (confirmData.isRegulated !== 'true') {
        confirmData.regulatorName = undefined
        confirmData.regulatorJurisdiction = undefined
      }
      if (confirmData.isPubliclyListed !== 'true') {
        confirmData.listedExchange = undefined
        confirmData.tickerSymbol = undefined
      }
    } else {
      confirmData.isRegulated = undefined
      confirmData.regulatorName = undefined
      confirmData.regulatorJurisdiction = undefined
      confirmData.isPubliclyListed = undefined
      confirmData.listedExchange = undefined
      confirmData.tickerSymbol = undefined
    }
    onConfirm({ data: confirmData, displayData: confirmDisplayData })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Box flexCol gap={16}>
        <Box ref={el => (fieldRefs.current.businessName = el)}>
          <Input
            {...formFields?.businessName}
            placeholder={t('embeddedCustomers.businessFormPlaceholder.businessName')}
            testId="business-name"
          />
        </Box>
        <Box ref={el => (fieldRefs.current.businessDescription = el)}>
          <Input
            {...formFields?.businessDescription}
            testId="business-description"
            Tooltip={
              <Tooltip
                type="complex"
                bodyContent={t('embeddedCustomers.businessFormTooltip.businessDescription')}
              />
            }
          />
        </Box>
        <Box ref={el => (fieldRefs.current.industry = el)}>
          <ComboboxGroup {...formFields?.industry} />
        </Box>
        <Box ref={el => (fieldRefs.current.countryOfIncorporation = el)}>
          <Combobox {...formFields?.countryOfIncorporation} />
        </Box>
        {formValues?.countryOfIncorporation === CountryCodes.US && (
          <Box ref={el => (fieldRefs.current.stateCode = el)}>
            <Combobox {...formFields?.stateCode} />
          </Box>
        )}
        {relianceAddressFields()}
        <Box ref={el => (fieldRefs.current.monthlyExpectedVolumes = el)}>
          <Combobox {...formFields?.monthlyExpectedVolumes} />
        </Box>
        <Box ref={el => (fieldRefs.current.useCase = el)}>
          <Combobox
            {...formFields?.useCase}
            Tooltip={<Tooltip type="complex" bodyContent={t('customers.serviceTypeTooltip')} />}
          />
        </Box>
        <Box ref={el => (fieldRefs.current.externalReference = el)}>
          <Input
            {...formFields?.externalReference}
            testId="business-external-reference"
            optionalText={t('optional')}
            Tooltip={
              <Tooltip type="complex" bodyContent={t('customers.externalReferenceTooltip')} />
            }
          />
        </Box>
        {relianceFields()}
        {showPaxosUsStatesFields && !isReliance && (
          <>
            <Box ref={el => (fieldRefs.current.isRegulated = el)}>
              <Radio
                name="isRegulated"
                label={t('embeddedCustomers.businessForm.isRegulated')}
                options={BOOLEAN_RADIO_OPTIONS}
                value={formValues?.isRegulated ?? ''}
                onChange={value =>
                  handleRadioChange('isRegulated', value, [
                    'regulatorName',
                    'regulatorJurisdiction',
                  ])
                }
                error={form?.formState?.errors?.isRegulated?.message}
                testId="is-regulated"
              />
            </Box>
            {formValues?.isRegulated === 'true' && (
              <>
                <Box ref={el => (fieldRefs.current.regulatorName = el)}>
                  <Input {...formFields?.regulatorName} testId="regulator-name" />
                </Box>
                <Box ref={el => (fieldRefs.current.regulatorJurisdiction = el)}>
                  <Combobox {...formFields?.regulatorJurisdiction} />
                </Box>
              </>
            )}
            <Box ref={el => (fieldRefs.current.isPubliclyListed = el)}>
              <Radio
                name="isPubliclyListed"
                label={t('embeddedCustomers.businessForm.isPubliclyListed')}
                options={BOOLEAN_RADIO_OPTIONS}
                value={formValues?.isPubliclyListed ?? ''}
                onChange={value =>
                  handleRadioChange('isPubliclyListed', value, ['listedExchange', 'tickerSymbol'])
                }
                error={form?.formState?.errors?.isPubliclyListed?.message}
                testId="is-publicly-listed"
              />
            </Box>
            {formValues?.isPubliclyListed === 'true' && (
              <>
                <Box ref={el => (fieldRefs.current.listedExchange = el)}>
                  <Input {...formFields?.listedExchange} testId="listed-exchange" />
                </Box>
                <Box ref={el => (fieldRefs.current.tickerSymbol = el)}>
                  <Input {...formFields?.tickerSymbol} testId="ticker-symbol" />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
